import { AppShell, Sidebar, VerificationPage } from '@suvoda-uat/ui-suvoda';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { LifecycleTopBar } from '../components/LifecycleTopBar.js';
import { VerificationView } from '../components/VerificationView.js';
import { signOut } from '../lib/auth.js';
import { isGenerationSucceeded } from '../lib/generation.js';
import { createLifecycleBeforeLoad } from '../lib/lifecycle.js';
import { navTargetFor, visibleNavItems } from '../lib/nav.js';
import {
  useCancelGeneration,
  useCurrentGenerationJob,
  useStartGeneration,
  useTrial,
  useTrialPack,
} from '../lib/queries.js';

export const verificationBeforeLoad = createLifecycleBeforeLoad('/verification');

export const Route = createFileRoute('/verification')({
  validateSearch: (search: Record<string, unknown>): { trialId: string } => ({
    trialId: typeof search['trialId'] === 'string' ? search['trialId'] : '',
  }),
  beforeLoad: verificationBeforeLoad,
  component: VerificationRoute,
});

function VerificationRoute() {
  const navigate = useNavigate();
  const { trialId } = Route.useSearch();
  const trial = useTrial(trialId);
  const job = useCurrentGenerationJob(trialId);
  const pack = useTrialPack(trialId);
  const cancel = useCancelGeneration(trialId);
  const start = useStartGeneration(trialId);

  // On success a draft Test Script exists → go to the (stubbed) Assembly view,
  // carrying the trialId so the Download flow (US3) can target this Trial.
  useEffect(() => {
    if (isGenerationSucceeded(job.data)) void navigate({ to: '/uat', search: { trialId } });
  }, [job.data, navigate, trialId]);

  const backToUpload = () => navigate({ to: '/upload', search: { trialId } });
  // Best-effort cancel (R2): request it for the in-flight job; the poll keeps
  // running until the honoured terminal state arrives.
  const requestCancel = () => {
    const jobId = job.data?.jobId;
    if (jobId !== undefined) cancel.mutate(jobId);
  };

  return (
    <AppShell
      sidebar={
        <Sidebar
          items={visibleNavItems()}
          activeId="uat"
          searchPlaceholder="What are you looking for?"
          onNavigate={(id) => {
            const to = navTargetFor(id);
            if (to !== undefined) void navigate({ to });
          }}
          onSignOut={() => signOut()}
        />
      }
      topbar={<LifecycleTopBar currentStep="verification" trial={trial.data} />}
      contentClassName="px-8 pb-7 pt-3"
    >
      <VerificationPage onCancel={backToUpload}>
        <VerificationView
          job={job.data}
          pack={pack.data}
          rerunPending={start.isPending}
          adjustDocumentsHref={`/upload?trialId=${encodeURIComponent(trialId)}`}
          onRequestCancel={requestCancel}
          onBackToUpload={backToUpload}
          onRerunGeneration={() => start.mutate()}
        />
      </VerificationPage>
    </AppShell>
  );
}
